import { devError } from '@/lib/logger';
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize free-form time input (e.g. "22:30", "22.30", "2230", "9:5") to "HH:MM" or null
function normalizeTime(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  // 1) HH:MM or H:MM with colon, dot or comma
  const colonMatch = trimmed.match(/^(\d{1,2})[:.,](\d{1,2})$/);
  if (colonMatch) {
    const h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    return null;
  }
  // 2) Compact "HHMM" (e.g. "2230")
  const compactMatch = trimmed.match(/^(\d{3,4})$/);
  if (compactMatch) {
    const padded = compactMatch[1].padStart(4, '0');
    const h = parseInt(padded.slice(0, 2), 10);
    const m = parseInt(padded.slice(2), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  return null;
}

function timeToMinutes(value) {
  const norm = normalizeTime(value);
  if (!norm) return null;
  const [h, m] = norm.split(':').map(Number);
  return h * 60 + m;
}

function formatMinutes(min) {
  const sign = min > 0 ? '+' : '';
  if (Math.abs(min) >= 60) {
    const h = Math.floor(Math.abs(min) / 60);
    const m = Math.abs(min) % 60;
    return `${sign}${min < 0 ? '-' : ''}${h}h${m > 0 ? `${m}min` : ''}`;
  }
  return `${sign}${min}min`;
}

export default function AdjustmentDialog({ open, onOpenChange, prefill = {}, onCreated }) {
  const [date, setDate] = useState('');
  const [originalTimeOut, setOriginalTimeOut] = useState('');
  const [newTimeOut, setNewTimeOut] = useState('');
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initialDate = prefill.date || new Date().toISOString().slice(0, 10);
    setDate(initialDate);
    setOriginalTimeOut(prefill.original_time_out || '');
    setNewTimeOut('');
    setJustification('');

    if (!prefill.original_time_out && prefill.employee_id) {
      axios
        .get(`${API}/employees/${prefill.employee_id}/scheduled-end`, {
          params: { date: initialDate },
          withCredentials: true,
        })
        .then((res) => {
          if (res.data?.scheduled_end) setOriginalTimeOut(res.data.scheduled_end);
        })
        .catch((err) => {
          devError('adj-quick-prefill', err);
        });
    }
  }, [open, prefill.date, prefill.original_time_out, prefill.employee_id]);

  const delta = useMemo(() => {
    const o = timeToMinutes(originalTimeOut);
    const n = timeToMinutes(newTimeOut);
    if (o === null || n === null) return null;
    return n - o;
  }, [originalTimeOut, newTimeOut]);

  const handleSave = async () => {
    if (!prefill.employee_id || !prefill.location_id) { toast.error('Funcionário/local em falta'); return; }

    const original = normalizeTime(originalTimeOut);
    const newOut = normalizeTime(newTimeOut);

    if (!newOut) { toast.error('Saída ajustada inválida (use HH:MM, ex: 22:30)'); return; }
    if (!justification.trim()) { toast.error('Justificativa é obrigatória'); return; }

    let originalResolved = original;
    if (!originalResolved) {
      try {
        const r = await axios.get(`${API}/employees/${prefill.employee_id}/scheduled-end`, {
          params: { date }, withCredentials: true,
        });
        originalResolved = r.data?.scheduled_end || null;
      } catch (err) {
        devError('adj-quick-resolve-original', err);
      }
    }
    if (!originalResolved) { toast.error('Sem horário agendado — preencha "Saída original"'); return; }

    const oMin = timeToMinutes(originalResolved);
    const nMin = timeToMinutes(newOut);
    const deltaMin = nMin - oMin;
    if (deltaMin === 0) { toast.error('O novo horário deve ser diferente do original'); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/time-adjustments`, {
        employee_id: prefill.employee_id,
        location_id: prefill.location_id,
        date,
        original_time_out: originalResolved,
        new_time_out: newOut,
        delta_minutes: deltaMin,
        reason: deltaMin > 0 ? 'overtime' : 'early_exit',
        justification: justification.trim(),
      }, { withCredentials: true });
      toast.success(`Ajuste registado (${formatMinutes(deltaMin)})`);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar ajuste');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="adj-quick-desc">
        <DialogHeader>
          <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
            Ajustar Saída
          </DialogTitle>
          <DialogDescription id="adj-quick-desc">
            {prefill.employee_name ? <strong>{prefill.employee_name}</strong> : 'Funcionário'} — modificação registada com seu nome para auditoria.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Data</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="adj-quick-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Saída original</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="HH:MM (ex: 18:00)"
                value={originalTimeOut}
                onChange={(e) => setOriginalTimeOut(e.target.value)}
                onBlur={(e) => {
                  const norm = normalizeTime(e.target.value);
                  if (norm) setOriginalTimeOut(norm);
                }}
                data-testid="adj-quick-original"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1">Horário agendado da escala (auto-preenchido)</p>
            </div>
            <div>
              <Label>Saída ajustada *</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="HH:MM (ex: 22:30)"
                value={newTimeOut}
                onChange={(e) => setNewTimeOut(e.target.value)}
                onBlur={(e) => {
                  const norm = normalizeTime(e.target.value);
                  if (norm) setNewTimeOut(norm);
                }}
                data-testid="adj-quick-new"
              />
            </div>
          </div>
          <div className="bg-[#F3F4F6] p-3 text-sm">
            <strong>Delta:</strong>{' '}
            <span
              className={`mono font-bold ${delta > 0 ? 'text-[#059669]' : delta < 0 ? 'text-[#DC2626]' : 'text-[#6B7280]'}`}
              data-testid="adj-quick-delta"
            >
              {delta === null ? '— preencha os horários (HH:MM)' : delta === 0 ? '0min' : formatMinutes(delta)}
            </span>
            {delta !== null && delta !== 0 && (
              <span className="text-[#6B7280] ml-2">
                ({delta > 0 ? 'Hora Extra' : 'Saída Antecipada'})
              </span>
            )}
          </div>
          <div>
            <Label>Justificativa expressa *</Label>
            <Textarea
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Ex: Cobertura de evento, autorizado por gerente..."
              data-testid="adj-quick-justification"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="adj-quick-submit" className="bg-[#0029FF] hover:bg-[#0022CC]">
            {saving ? 'Salvando...' : 'Salvar Ajuste'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
